import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useTenant } from '@/hooks/useTenant';
import type { SMSMessage, SMSMessageWithRelations, SMSThread, SendSMSRequest, SendSMSResponse } from '@/types/sms';

// Hook for fetching SMS messages
export function useSMSMessages(options: {
  leadId?: string;
  customerId?: string;
  trackingNumber?: string;
  limit?: number;
}) {
  const { leadId, customerId, trackingNumber, limit = 100 } = options;

  return useQuery({
    queryKey: ['sms-messages', { leadId, customerId, trackingNumber }],
    queryFn: async (): Promise<SMSMessageWithRelations[]> => {
      let query = supabase
        .from('sms_messages')
        .select(`
          *,
          leads:lead_id(customer_name, lead_number),
          customers:customer_id(customer_name),
          campaigns:campaign_id(name),
          profiles!sms_messages_sender_id_fkey(full_name)
        `)
        .order('sent_at', { ascending: true })
        .limit(limit);

      if (leadId) {
        query = query.eq('lead_id', leadId);
      }

      if (customerId) {
        query = query.eq('customer_id', customerId);
      }

      if (trackingNumber) {
        query = query.eq('tracking_number', trackingNumber);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as SMSMessageWithRelations[];
    },
    enabled: !!(leadId || customerId),
  });
}

// Hook for fetching SMS threads (grouped conversations)
export function useSMSThreads(options: { limit?: number } = {}) {
  const { limit = 50 } = options;

  return useQuery({
    queryKey: ['sms-threads'],
    queryFn: async (): Promise<SMSThread[]> => {
      // Fetch recent messages grouped by lead/customer
      const { data: messages, error } = await supabase
        .from('sms_messages')
        .select(`
          *,
          leads:lead_id(customer_name, lead_number, customer_phone),
          customers:customer_id(customer_name, primary_phone)
        `)
        .order('sent_at', { ascending: false })
        .limit(500);

      if (error) throw error;

      // Group messages into threads
      const threadMap = new Map<string, SMSThread>();
      
      for (const msg of (messages || [])) {
        // Create thread key based on lead_id or customer_id + tracking_number
        const threadKey = msg.lead_id 
          ? `lead:${msg.lead_id}:${msg.tracking_number || 'default'}`
          : msg.customer_id 
            ? `customer:${msg.customer_id}:${msg.tracking_number || 'default'}`
            : `phone:${msg.direction === 'inbound' ? msg.from_number : msg.to_number}`;

        if (!threadMap.has(threadKey)) {
          const contactName = msg.leads?.customer_name || msg.customers?.customer_name || 'Unknown';
          const contactNumber = msg.direction === 'inbound' ? msg.from_number : msg.to_number;

          threadMap.set(threadKey, {
            id: threadKey,
            lead_id: msg.lead_id || undefined,
            customer_id: msg.customer_id || undefined,
            tracking_number: msg.tracking_number || undefined,
            contact_name: contactName,
            contact_number: contactNumber,
            last_message: msg.content || '',
            last_message_at: msg.sent_at,
            unread_count: 0, // TODO: Implement unread tracking
            messages: [],
          });
        }

        const thread = threadMap.get(threadKey)!;
        thread.messages.push(msg);
      }

      // Convert to array and sort by last message
      const threads = Array.from(threadMap.values())
        .sort((a, b) => new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime())
        .slice(0, limit);

      // Reverse messages within each thread to be chronological
      threads.forEach(thread => {
        thread.messages.reverse();
      });

      return threads;
    },
  });
}

// Hook for sending SMS
export function useSendSMS() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: SendSMSRequest): Promise<SendSMSResponse> => {
      const { data, error } = await supabase.functions.invoke('send-sms', {
        method: 'POST',
        body: request,
      });

      if (error) {
        // FunctionsHttpError swallows the response body — extract it so the toast shows the real reason
        let detail = error.message;
        const ctx = (error as { context?: Response }).context;
        if (ctx && typeof ctx.json === 'function') {
          try {
            const body = await ctx.clone().json();
            if (body?.error) {
              detail = body.details ? `${body.error}: ${body.details}` : body.error;
            }
          } catch {
            try {
              const text = await ctx.text();
              if (text) detail = text;
            } catch { /* keep generic message */ }
          }
        }
        throw new Error(detail);
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to send SMS');
      }

      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sms-messages'] });
      queryClient.invalidateQueries({ queryKey: ['sms-threads'] });
      if (variables.lead_id) {
        queryClient.invalidateQueries({ queryKey: ['lead-activities', variables.lead_id] });
      }
      toast({ title: 'SMS sent successfully' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to send SMS',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// Hook for SMS settings (derived from company settings)
export function useSMSSettings() {
  const { company, isLoadingCompany } = useTenant();

  const data = useMemo(() => company ? {
    enabled: company.call_tracking_enabled && company.sms_enabled,
    defaultNumber: company.default_caller_id,
  } : undefined, [company]);

  return { data, isLoading: isLoadingCompany };
}
